/**
 * Wraps MutationObserver, requestAnimationFrame, and addEventListener to defer
 * third-party callbacks from blocking the main thread during user interactions.
 *
 * Problem: Third-party scripts (e.g. Google Sign-In, GTM tags) create
 * MutationObservers, requestAnimationFrame callbacks, and event listeners that
 * force expensive synchronous layout recalculation, blocking the main thread
 * during user interactions (hurting INP).
 *
 * Solution: Call `installDeferredObserver()` before loading third-party scripts.
 * - MutationObserver callbacks from third-party scripts are deferred to
 *   `requestIdleCallback` (first-party MOs are left untouched)
 * - requestAnimationFrame is patched so third-party rAF callbacks run during
 *   idle time instead of blocking the rendering pipeline
 * - addEventListener is patched so third-party click/pointer event handlers
 *   run during idle time instead of during the interaction's processing time
 */

let installed = false
let originalMO: typeof MutationObserver | null = null
let originalRAF: typeof requestAnimationFrame | null = null
let originalCancelRAF: typeof cancelAnimationFrame | null = null
let originalAddEventListener:
  | typeof EventTarget.prototype.addEventListener
  | null = null
let originalRemoveEventListener:
  | typeof EventTarget.prototype.removeEventListener
  | null = null

// Event types whose third-party handlers get deferred to idle time.
// These are the events where forced layout recalculation is most expensive
// (GSI's click handler costs ~106ms on first click after DOM changes).
const DEFERRED_EVENT_TYPES = new Set([
  'click',
  'mousedown',
  'mouseup',
  'pointerdown',
  'pointerup',
  'touchstart',
  'touchend',
])

// Track third-party MutationObserver instances created through the Proxy
// so we can disconnect specific ones (e.g. GSI's observers after prompt).
const trackedObservers: MutationObserver[] = []

// Our own code's callbacks should run normally (they're intentional).
// Only third-party callbacks get deferred.
const FIRST_PARTY_PATTERNS = [
  'local.pitchup.com',
  'www.pitchup.com',
  'www.stagingpitchup.com',
  'localhost',
  // Vue/Nuxt internals
  '_nuxt/',
  'node_modules',
]

function isFirstPartyCall(): boolean {
  // Use Error stack trace to find the actual caller.
  // Skip 3 frames: Error, isFirstPartyCall, caller's wrapper (deferredRAF
  // or Proxy construct trap). Frame 3+ is the code that actually called
  // requestAnimationFrame / new MutationObserver.
  const stack = new Error().stack || ''
  const lines = stack.split('\n').slice(3, 7)
  return lines.some((line) =>
    FIRST_PARTY_PATTERNS.some((pattern) => line.includes(pattern)),
  )
}

/**
 * Replace the global MutationObserver with a Proxy that defers third-party
 * callbacks to idle time, and patch requestAnimationFrame to defer third-party
 * rAF callbacks to idle time.
 *
 * First-party code (matched by FIRST_PARTY_PATTERNS) is not affected —
 * their MO and rAF callbacks run normally.
 *
 * Captures original constructors at call time (not module import time)
 * to avoid SSR issues where `window` is undefined.
 */
export function installDeferredObserver() {
  if (typeof window === 'undefined' || installed) return

  const Orig = window.MutationObserver
  if (!Orig) return

  installed = true
  originalMO = Orig

  // --- Patch MutationObserver ---
  // Use a Proxy to intercept `new MutationObserver(callback)` calls.
  // The `construct` trap fires on `new`, creating a real MutationObserver
  // with a wrapped callback that defers to requestIdleCallback.
  // Proxy is the most robust interception — it preserves instanceof checks,
  // prototype chain, and works regardless of how the constructor is accessed.
  window.MutationObserver = new Proxy(Orig, {
    construct(
      target: typeof MutationObserver,
      args: [MutationCallback],
    ): MutationObserver {
      const [callback] = args

      // First-party MO: create normally without wrapping or tracking.
      // e.g. useHydrationStore creates MOs on every SPA navigation —
      // these must not be deferred or disconnected.
      if (isFirstPartyCall()) {
        return new target(callback)
      }

      // Third-party MO: defer callbacks to idle time and track for disconnect.
      //
      // LIMITATION: Only the most recent mutation batch is delivered. When
      // multiple MO callbacks fire before the idle callback runs, earlier
      // batches are discarded (the pending idle callback is cancelled and
      // rescheduled with the latest mutations). This is safe for scripts
      // that re-scan the full DOM on each callback (e.g. GSI, GTM) but
      // could miss intermediate mutations for scripts that process
      // incremental changes.
      let pendingIdleId: ReturnType<typeof requestIdleCallback> | null = null

      const observer = new target(
        (mutations: MutationRecord[], obs: MutationObserver) => {
          // Cancel any pending idle callback — we'll schedule a fresh one
          // with the latest mutations. For third-party auto-discovery scripts
          // (like GSI), only the final batch matters since they re-scan the DOM.
          if (pendingIdleId !== null) {
            cancelIdleCallback(pendingIdleId)
          }

          pendingIdleId = requestIdleCallback(
            () => {
              pendingIdleId = null
              callback(mutations, obs)
            },
            { timeout: 2000 },
          )
        },
      )
      trackedObservers.push(observer)
      return observer
    },
  })

  // --- Patch requestAnimationFrame ---
  // Third-party scripts (GSI) register rAF callbacks that force synchronous
  // layout reads (offsetWidth, getBoundingClientRect, etc.), costing ~110ms.
  // We defer third-party rAF callbacks to requestIdleCallback so they run
  // during idle time instead of blocking the rendering pipeline.
  originalRAF = window.requestAnimationFrame
  originalCancelRAF = window.cancelAnimationFrame

  // Map deferred rAF IDs to their idle callback IDs
  const deferredMap = new Map<number, ReturnType<typeof requestIdleCallback>>()
  let nextFakeId = -1 // negative IDs to avoid collisions with real rAF IDs

  window.requestAnimationFrame = function deferredRAF(
    callback: FrameRequestCallback,
  ): number {
    // First-party rAF callbacks run normally
    if (isFirstPartyCall()) {
      return originalRAF!.call(window, callback)
    }

    // Third-party rAF callbacks are deferred to idle time
    const fakeId = nextFakeId--
    const idleId = requestIdleCallback(
      () => {
        deferredMap.delete(fakeId)
        callback(performance.now())
      },
      { timeout: 2000 },
    )
    deferredMap.set(fakeId, idleId)
    return fakeId
  }

  window.cancelAnimationFrame = function deferredCancelRAF(id: number): void {
    const idleId = deferredMap.get(id)
    if (idleId !== undefined) {
      cancelIdleCallback(idleId)
      deferredMap.delete(id)
    } else {
      originalCancelRAF!.call(window, id)
    }
  }

  // --- Patch addEventListener ---
  // Third-party scripts (GSI, GTM tags) register click/pointer event listeners
  // on the document that force synchronous layout recalculation during user
  // interactions. GSI's click handler alone costs ~106ms on the first click
  // after significant DOM changes.
  //
  // We wrap third-party handlers for interaction events so the original
  // callback runs in requestIdleCallback instead of during the event's
  // processing time. The event object is captured at dispatch time so the
  // deferred callback still has access to target, type, etc.
  originalAddEventListener = EventTarget.prototype.addEventListener
  originalRemoveEventListener = EventTarget.prototype.removeEventListener
  const origRemove = EventTarget.prototype.removeEventListener

  // Map original callbacks to their wrapped versions so removeEventListener
  // can find and remove the correct handler.
  const wrappedListeners = new WeakMap<
    EventListenerOrEventListenerObject,
    EventListenerOrEventListenerObject
  >()

  EventTarget.prototype.addEventListener = function deferredAddEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ): void {
    if (!listener || !DEFERRED_EVENT_TYPES.has(type) || isFirstPartyCall()) {
      return originalAddEventListener!.call(this, type, listener, options)
    }

    // Wrap the third-party listener to defer execution to idle time
    const originalListener = listener
    const wrapped: EventListener = (event: Event) => {
      requestIdleCallback(
        () => {
          if (typeof originalListener === 'function') {
            originalListener(event)
          } else {
            originalListener.handleEvent(event)
          }
        },
        { timeout: 2000 },
      )
    }

    wrappedListeners.set(originalListener, wrapped)
    return originalAddEventListener!.call(this, type, wrapped, options)
  }

  EventTarget.prototype.removeEventListener =
    function deferredRemoveEventListener(
      type: string,
      listener: EventListenerOrEventListenerObject | null,
      options?: boolean | EventListenerOptions,
    ): void {
      if (!listener) {
        return origRemove.call(this, type, listener, options)
      }
      const wrapped = wrappedListeners.get(listener)
      if (wrapped) {
        wrappedListeners.delete(listener)
        return origRemove.call(this, type, wrapped, options)
      }
      return origRemove.call(this, type, listener, options)
    }
}

/**
 * Disconnect all tracked third-party MutationObservers.
 * Call after GSI prompt() — once OneTap is shown, GSI's observers serve
 * no purpose. Disconnecting them stops the MO→rAF chain that causes
 * ~107ms layout recalculations on every DOM mutation.
 *
 * Does NOT affect the rAF deferral — third-party rAF callbacks continue
 * to be deferred to idle time (needed for GTM tags loaded later).
 */
export function disconnectTrackedObservers() {
  for (const observer of trackedObservers) {
    observer.disconnect()
  }
  trackedObservers.length = 0
}

/**
 * Clear the tracked observers list WITHOUT disconnecting them.
 * Call before loading a new third-party script (e.g. GSI) so that
 * a subsequent `disconnectTrackedObservers()` only disconnects
 * observers created by that script — not earlier ones (e.g. CookieYes).
 */
export function resetTrackedObservers() {
  trackedObservers.length = 0
}

/**
 * Restore the original MutationObserver constructor and requestAnimationFrame.
 * Observers already created with the deferred version keep their behavior.
 */
export function uninstallDeferredObserver() {
  if (typeof window === 'undefined' || !installed) return
  installed = false
  if (originalMO) {
    window.MutationObserver = originalMO
    originalMO = null
  }
  if (originalRAF) {
    window.requestAnimationFrame = originalRAF
    originalRAF = null
  }
  if (originalCancelRAF) {
    window.cancelAnimationFrame = originalCancelRAF
    originalCancelRAF = null
  }
  if (originalAddEventListener) {
    EventTarget.prototype.addEventListener = originalAddEventListener
    originalAddEventListener = null
  }
  if (originalRemoveEventListener) {
    EventTarget.prototype.removeEventListener = originalRemoveEventListener
    originalRemoveEventListener = null
  }
}
